// Reusable FilterBar component for consistent filter UI across the application
// Based on the transaction filter bar design with Apple-inspired minimalist styling
// 
// Usage:
//   <FilterBar filters={[
//     { type: 'select', label: 'Status', value: status, onChange: setStatus, options: [...] },
//     { type: 'react-select', label: 'Branch', value: branch, onChange: setBranch, options: [...] },
//   ]} />

import React from "react";
import Select from "react-select";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";

function DateFilterInput({ label, value, onChange, min, max, minWidth, className }) {
    const inputRef = React.useRef(null);

    const openPicker = () => {
        const input = inputRef.current;
        if (!input) return;

        input.focus();
        if (typeof input.showPicker === "function") {
            input.showPicker();
            return;
        }
        input.click();
    };

    return (
        <div className={`shrink-0 flex flex-col ${className}`} style={{ minWidth }}>
            <label className="text-[13px] font-semibold mb-1 text-gray-700 pl-1">{label}</label>
            <div className="relative">
                <input
                    ref={inputRef}
                    type="date"
                    className="h-10 w-full appearance-none rounded-xl border-0 bg-[#f6f6f8] py-2 pl-3 pr-11 text-[15px] text-gray-900 focus:ring-2 focus:ring-primary"
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    min={min}
                    max={max}
                />
                <button
                    type="button"
                    onClick={openPicker}
                    className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-gray-500 hover:bg-white hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary"
                    aria-label={`Open ${label} picker`}
                    tabIndex={-1}
                >
                    <CalendarDays size={18} strokeWidth={2} />
                </button>
            </div>
        </div>
    );
}

/**
 * FilterBar Component
 * @param {Array} filters - Array of filter configurations
 * @param {ReactNode} extraActions - Additional buttons/actions to display at the end (e.g., view toggles)
 */
function FilterBar({ filters = [], extraActions = null }) {
    const scrollRef = React.useRef(null);
    const [showLeftHint, setShowLeftHint] = React.useState(false);
    const [showRightHint, setShowRightHint] = React.useState(false);

    React.useEffect(() => {
        const node = scrollRef.current;
        if (!node) return;

        const updateHints = () => {
            const isMobile = window.innerWidth < 768;
            if (!isMobile) {
                setShowLeftHint(false);
                setShowRightHint(false);
                return;
            }

            const { scrollLeft, scrollWidth, clientWidth } = node;
            const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);
            const threshold = 8;

            setShowLeftHint(scrollLeft > threshold);
            setShowRightHint(scrollLeft < maxScrollLeft - threshold);
        };

        updateHints();
        node.addEventListener("scroll", updateHints, { passive: true });
        window.addEventListener("resize", updateHints);

        return () => {
            node.removeEventListener("scroll", updateHints);
            window.removeEventListener("resize", updateHints);
        };
    }, [filters.length, extraActions]);

    // Shared styles for react-select
    const reactSelectStyles = {
        control: (base) => ({
            ...base,
            background: '#f6f6f8',
            border: 'none',
            boxShadow: 'none',
            minHeight: 36,
            borderRadius: 10,
            fontSize: 15,
        }),
        menu: (base) => ({ ...base, borderRadius: 10, fontSize: 15, zIndex: 30 }),
        menuPortal: (base) => ({ ...base, zIndex: 30 }),
        option: (base, state) => ({
            ...base,
            background: state.isSelected ? '#e5e5ea' : state.isFocused ? '#f0f0f3' : '#fff',
            color: '#222',
            borderRadius: 8,
            fontSize: 15,
        }),
    };

    return (
        <div className="relative mb-1 rounded-xl bg-[#f6f6f8] shadow-md">
            <div
                ref={scrollRef}
                className="filterbar-scroll overflow-x-auto overflow-y-hidden rounded-xl p-2 md:overflow-x-visible md:overflow-y-visible"
            >
                <div className="flex min-w-max flex-nowrap items-end gap-3 pr-24 md:min-w-0 md:flex-wrap md:pr-0">
            {filters.map((filter, index) => {
                const { type, label, value, onChange, options, placeholder, isSearchable = false, minWidth = '120px', hide = false, className = '', min, max } = filter;

                // Skip hidden filters
                if (hide) return null;

                // Native select dropdown
                if (type === 'select') {
                    return (
                        <div key={index} className={`shrink-0 flex flex-col ${className}`} style={{ minWidth }}>
                            <label className="text-[13px] font-semibold mb-1 text-gray-700 pl-1">{label}</label>
                            <select
                                className="bg-[#f6f6f8] rounded-xl px-3 py-2 text-[15px] border-0 focus:ring-2 focus:ring-primary"
                                value={value}
                                onChange={(e) => onChange(e.target.value)}
                            >
                                {options.map((opt, idx) => (
                                    <option key={idx} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    );
                }

                // React-select dropdown (for searchable/complex options)
                if (type === 'react-select') {
                    return (
                        <div key={index} className={`shrink-0 flex flex-col ${className}`} style={{ minWidth }}>
                            <label className="text-[13px] font-semibold mb-1 text-gray-700 pl-1">{label}</label>
                            <Select
                                classNamePrefix="react-select"
                                styles={reactSelectStyles}
                                options={options}
                                value={value}
                                onChange={(opt) => onChange(opt ? opt.value : '')}
                                isSearchable={isSearchable}
                                placeholder={placeholder || label}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        </div>
                    );
                }

                if (type === 'date') {
                    return (
                        <DateFilterInput
                            key={index}
                            label={label}
                            value={value}
                            onChange={onChange}
                            min={min}
                            max={max}
                            minWidth={minWidth}
                            className={className}
                        />
                    );
                }

                return null;
            })}

            {/* Extra actions (e.g., view toggles) */}
            {extraActions && (
                <div className="ml-auto hidden shrink-0 gap-2 md:flex">
                    {extraActions}
                </div>
            )}
                </div>
            </div>

            {extraActions && (
                <div className="absolute bottom-2 right-2 z-20 flex gap-2 md:hidden">
                    {extraActions}
                </div>
            )}

            {showLeftHint && (
                <div className="pointer-events-none absolute left-2 top-1 z-10 md:hidden">
                    <div className="flex items-center gap-0.5 rounded-full bg-white/85 px-1.5 py-1 text-[10px] font-medium text-slate-400 shadow-sm ring-1 ring-slate-200/80 backdrop-blur-sm animate-pulse">
                        <ChevronLeft className="h-3 w-3 animate-[bounceLeft_1.2s_ease-in-out_infinite]" />
                        <span>Back</span>
                    </div>
                </div>
            )}

            {showRightHint && (
            <div className="pointer-events-none absolute right-2 top-1 z-10 md:hidden">
                <div className="flex items-center gap-0.5 rounded-full bg-white/85 px-1.5 py-1 text-[10px] font-medium text-slate-400 shadow-sm ring-1 ring-slate-200/80 backdrop-blur-sm animate-pulse">
                    <span>More</span>
                    <ChevronRight className="h-3 w-3 animate-[bounceX_1.2s_ease-in-out_infinite]" />
                </div>
            </div>
            )}

            <style>{`
                .filterbar-scroll {
                    scrollbar-width: none;
                    -ms-overflow-style: none;
                }
                .filterbar-scroll::-webkit-scrollbar {
                    display: none;
                }
                @keyframes bounceX {
                    0%, 100% { transform: translateX(0); }
                    50% { transform: translateX(3px); }
                }
                @keyframes bounceLeft {
                    0%, 100% { transform: translateX(0); }
                    50% { transform: translateX(-3px); }
                }
            `}</style>
        </div>
    );
}

export default FilterBar;
